import React, { useState, useEffect, useContext } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import Board from 'react-trello';
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { useHistory } from 'react-router-dom';
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import { Badge, Tooltip, Typography, Button, TextField, Box } from "@material-ui/core";
import { format, isSameDay, parseISO } from "date-fns";
import { Can } from "../../components/Can";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    flex: 1,
    overflow: "hidden",
  },
  filtersBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  filtersGroup: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  kanbanContainer: {
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    flex: 1,
    overflowX: "auto",
    overflowY: "hidden",
    ...theme.scrollbarStyles,
    "& > div": {
      height: "100%",
    },
  },
  funnelHint: {
    width: "100%",
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 1,
    padding: 1,
    fontWeight: 'bold',
    borderRadius: 3,
    fontSize: "0.6em",
  },
  lastMessageTime: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    marginLeft: "auto",
    color: theme.palette.text.secondary,
  },
  lastMessageTimeUnread: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    color: theme.palette.success.main,
    fontWeight: "bold",
    marginLeft: "auto"
  },
  cardButton: {
    marginRight: theme.spacing(1),
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  dateInput: {
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const theme = useTheme(); // Obter o tema atual
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketNot, setTicketNot] = useState(0);
  const [file, setFile] = useState({ lanes: [] });
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  const jsonString = user.queues.map(queue => queue.UserQueue.queueId);

  useEffect(() => {
    fetchTags();
  }, [user]);

  const fetchTags = async () => {
    try {
      const response = await api.get("/tag/kanban/");
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets();
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async () => {
    try {
      const { data } = await api.get("/ticket/kanban", {
        params: {
          queueIds: JSON.stringify(jsonString),
          startDate: startDate,
          endDate: endDate,
        }
      });
      setTickets(data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;
    const onAppMessage = (data) => {
      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        fetchTickets();
      }
    };
    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, startDate, endDate]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle", fontSize: "16px" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle", fontSize: "16px" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle", fontSize: "16px" }} />
      default:
        return "error";
    }
  };

  const popularCards = (jsonString) => {
    const SDR_FUNNEL_ORDER = [
      "SDR - Novo Lead",
      "SDR - Qualificando",
      "SDR - Demo Agendada",
      "SDR - Nutricao",
      "SDR - Parceiro",
      "SDR - Handoff Comercial",
    ];

    const sortedTags = [...tags].sort((a, b) => {
      const indexA = SDR_FUNNEL_ORDER.indexOf(a.name);
      const indexB = SDR_FUNNEL_ORDER.indexOf(b.name);
      if (indexA === -1 && indexB === -1) {
        return String(a.name || "").localeCompare(String(b.name || ""));
      }
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    const untaggedTickets = tickets.filter(ticket => ticket.tags.length === 0);

    const buildCard = (ticket) => ({
      id: ticket.id.toString(),
      label: "Ticket nº " + ticket.id.toString(),
      description: (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{ticket.contact.number}</span>
            <Typography
              className={Number(ticket.unreadMessages) > 0 ? classes.lastMessageTimeUnread : classes.lastMessageTime}
              component="span"
              variant="body2"
            >
              {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
              ) : (
                <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
              )}
            </Typography>
          </div>
          <div style={{ textAlign: 'left' }}>{ticket.lastMessage || " "}</div>
          <Button
            className={`${classes.button} ${classes.cardButton}`}
            onClick={() => {
              handleCardClick(ticket.uuid)
            }}>
            Ver Ticket
          </Button>
          <span style={{ marginRight: '8px' }} />
          {ticket?.user && (<Badge style={{ backgroundColor: "#000000" }} className={classes.connectionTag}>{ticket.user?.name.toUpperCase()}</Badge>)}
        </div>
      ),
      title: <>
        <Tooltip title={ticket.whatsapp?.name}>
          {IconChannel(ticket.channel)}
        </Tooltip> {ticket.contact.name}</>,
      draggable: true,
      href: "/tickets/" + ticket.uuid,
    });

    const funnelLanes = sortedTags.map(tag => {
      const filteredTickets = tickets.filter(ticket => {
        const tagIds = ticket.tags.map(ticketTag => ticketTag.id);
        return tagIds.includes(tag.id);
      });

      return {
        id: tag.id.toString(),
        title: tag.name,
        label: filteredTickets?.length.toString(),
        cards: filteredTickets.map(buildCard),
        style: { backgroundColor: tag.color, color: "white", minWidth: 280 }
      };
    });

    const lanes = [
      ...funnelLanes,
      {
        id: "lane0",
        title: i18n.t("tagsKanban.laneDefault"),
        label: untaggedTickets.length.toString(),
        cards: untaggedTickets.map(buildCard),
        style: { minWidth: 280 }
      },
    ];

    setFile({ lanes });
  };

  const handleCardClick = (uuid) => {
    history.push('/tickets/' + uuid);
  };

  useEffect(() => {
    popularCards(jsonString);
  }, [tags, tickets]);

  const handleCardMove = async (cardId, sourceLaneId, targetLaneId) => {
    try {
      await api.delete(`/ticket-tags/${targetLaneId}`);
      toast.success('Ticket Tag Removido!');
      await api.put(`/ticket-tags/${targetLaneId}/${sourceLaneId}`);
      toast.success('Ticket Tag Adicionado com Sucesso!');
      await fetchTickets(jsonString);
      popularCards(jsonString);
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddConnectionClick = () => {
    history.push('/tagsKanban');
  };

  return (
    <div className={classes.root}>
      <div className={classes.filtersBar}>
        <div className={classes.filtersGroup}>
          <TextField
            label="Data de início"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            size="small"
            className={classes.dateInput}
          />
          <TextField
            label="Data de fim"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            size="small"
            className={classes.dateInput}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearchClick}
          >
            Buscar
          </Button>
        </div>
        <Can role={user.profile} perform="dashboard:view" yes={() => (
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddConnectionClick}
          >
            {'+ Adicionar colunas'}
          </Button>
        )} />
      </div>
      <Typography className={classes.funnelHint}>
        Etapas do funil SDR à esquerda. Role horizontalmente para ver todas as colunas; tickets sem etapa ficam em &quot;{i18n.t("tagsKanban.laneDefault")}&quot;.
      </Typography>
      <div className={classes.kanbanContainer}>
        <Board
          data={file}
          onCardMoveAcrossLanes={handleCardMove}
          style={{
            backgroundColor: 'rgba(252, 252, 252, 0.03)',
            height: '100%',
            width: '100%',
            minWidth: 0,
          }}
        />
      </div>
    </div>
  );
};

export default Kanban;
